import { memo } from "react"
import { Handle, Node, NodeProps, Position } from "@xyflow/react"
import { Box, Heading, Separator, VStack, Text, HStack, Icon, Center } from "@chakra-ui/react"
import { IconType } from "react-icons"
import { FaTruckDroplet, FaBolt, FaDatabase, FaIndustry, FaPlug, FaTruck } from "react-icons/fa6"

export enum AssetType {
  Generator = "generator",
  Consumer = "consumer",
  Storage = "storage",
  Producer = "producer",
}

const DEFAULT_ASSET_ICONS: Record<AssetType, IconType> = {
  [AssetType.Generator]: FaBolt,
  [AssetType.Consumer]: FaTruck,
  [AssetType.Storage]: FaDatabase,
  [AssetType.Producer]: FaTruckDroplet,
}

interface BaseAssetData {
  label: string
  type: AssetType
  id: string
  icon?: IconType // Optional custom icon
  [key: string]: unknown
}

interface GeneratorAssetData {
  powerOutputKW?: number
  refuelHours?: number
}

enum ConsumerStatus {
  Filled = "Filled",
  Urgent = "Urgent",
  Offline = "Offline",
}

interface ConsumerAssetData {
  refuelHours?: number
  status?: ConsumerStatus
}

interface StorageAssetData {
  runDryHours?: number
  currentLevel?: number
}

interface ProducerAssetData {
  nextDeliveryHours?: number
}

type AssetData = GeneratorAssetData | ConsumerAssetData | StorageAssetData | ProducerAssetData
type AssetNode = Node<BaseAssetData, "asset">

const AssetNode = ({
  data,
  isConnectable,
  targetPosition = Position.Top,
  sourcePosition = Position.Bottom,
}: NodeProps<AssetNode>) => {
  const assetIcon = data.icon || DEFAULT_ASSET_ICONS[data.type]

  const renderAssetSpecificInfo = () => {
    switch (data.type) {
      case AssetType.Generator:
        const assetData: GeneratorAssetData = {
          powerOutputKW: 5,
          refuelHours: 48,
        }
        return (
          <>
            {assetData.powerOutputKW && (
              <Box display="flex" flexDirection="row" gap="3px" as="dl">
                <dd>Latest</dd>
                <Text fontWeight="bold" as="dt">
                  {assetData.powerOutputKW}kW
                </Text>
              </Box>
            )}
            {assetData.refuelHours && (
              <Box display="flex" flexDirection="row" gap="3px" as="dl">
                <dd>Refuel in</dd>
                <Text fontWeight="bold" as="dt">
                  {assetData.refuelHours}hrs
                </Text>
              </Box>
            )}
          </>
        )
      case AssetType.Consumer:
        const consumersData: Record<string, ConsumerAssetData> = {
          "excavator-1": {
            refuelHours: 5,
            status: ConsumerStatus.Filled,
          },
          "excavator-2": {
            refuelHours: 1,
            status: ConsumerStatus.Urgent,
          },
        }
        const consumerData = consumersData[data.id]
        if (!consumerData) return undefined
        return (
          <>
            {consumerData.status && (
              <Box display="flex" flexDirection="row" gap="3px" as="dl">
                <dd>Status</dd>
                <Text fontWeight="bold" as="dt">
                  {consumerData.status}
                </Text>
              </Box>
            )}
            {consumerData.refuelHours && (
              <Box display="flex" flexDirection="row" gap="3px" as="dl">
                <dd>Refuel in</dd>
                <Text fontWeight="bold" as="dt">
                  {consumerData.refuelHours}hrs
                </Text>
              </Box>
            )}
          </>
        )
      case AssetType.Storage:
        const storagesData: Record<string, StorageAssetData> = {
          bowser: {
            runDryHours: 24,
            currentLevel: 57,
          },
          hub: {
            runDryHours: 62,
            currentLevel: 24,
          },
        }
        const storageData = storagesData[data.id]
        if (!storageData) return undefined
        return (
          <>
            {storageData.currentLevel && (
              <Box display="flex" flexDirection="row" gap="3px" as="dl">
                <dd>Latest</dd>
                <Text fontWeight="bold" as="dt">
                  {storageData.currentLevel}%
                </Text>
              </Box>
            )}
            {storageData.currentLevel && (
              <Box display="flex" flexDirection="row" alignItems="center" gap={"3px"}>
                <Text fontWeight="bold">{storageData.runDryHours}h</Text>
                <Icon height="10px" width="10px" as={FaTruckDroplet} />
              </Box>
            )}
          </>
        )
      case AssetType.Producer:
        const productionData: ProducerAssetData = {
          nextDeliveryHours: 48,
        }
        return (
          <>
            {productionData.nextDeliveryHours && (
              <Box display="flex" flexDirection="row" gap="3px" as="dl">
                <dd>Delivery scheduled in</dd>
                <Text fontWeight="bold" as="dt">
                  {productionData.nextDeliveryHours}hrs
                </Text>
              </Box>
            )}
          </>
        )
    }
  }

  const shouldShowTargetHandle = [AssetType.Consumer, AssetType.Storage, AssetType.Generator].includes(data.type)
  const shouldShowSourceHandle = [AssetType.Producer, AssetType.Storage].includes(data.type)

  return (
    data && (
      <>
        {shouldShowTargetHandle && <Handle type="target" position={targetPosition} isConnectable={isConnectable} />}
        <VStack gap={"5px"} width="150px" padding="5px" color="white">
          <HStack justifyContent="space-between" width="100%">
            <HStack height="25px" gap={"3px"} alignItems="normal">
              <Icon height="15px" width="15px" as={assetIcon} />
              <Heading as={"h3"} size="xs">
                {data.label}
              </Heading>
            </HStack>
          </HStack>
          <Separator borderColor="white" width="100%" />
          <HStack fontSize="9px" justifyContent="space-between" width="100%">
            {renderAssetSpecificInfo()}
          </HStack>
        </VStack>
        {shouldShowSourceHandle && <Handle type="source" position={sourcePosition} isConnectable={isConnectable} />}
      </>
    )
  )
}

AssetNode.displayName = "AssetNode"

export default memo(AssetNode)
